import React from 'react';
import { ExternalLink, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface LinkPreviewData {
  title: string | null;
  description: string | null;
  image: string | null;
  domain: string;
  isYouTube?: boolean;
  videoId?: string | null;
}

interface LinkPreviewCardProps {
  preview: LinkPreviewData;
  url: string;
  onRemove?: () => void;
  isEditing?: boolean;
  isLoading?: boolean;
}

const LinkPreviewCard: React.FC<LinkPreviewCardProps> = ({
  preview,
  url,
  onRemove,
  isEditing = false,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="border rounded-lg p-4 flex items-center gap-3 bg-muted/50">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading preview...</span>
      </div>
    );
  }

  // YouTube embed
  if (preview.isYouTube && preview.videoId) {
    return (
      <div className="relative">
        {isEditing && onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 z-10"
            onClick={onRemove}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
        <div className="relative w-full pt-[56.25%] rounded-lg overflow-hidden bg-muted">
          <iframe
            src={`https://www.youtube.com/embed/${preview.videoId}`}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="YouTube video"
          />
        </div>
      </div>
    );
  }

  // Regular link preview
  const CardWrapper = isEditing ? 'div' : 'a';
  const cardProps = isEditing
    ? {}
    : {
        href: url,
        target: '_blank',
        rel: 'noopener noreferrer',
      };

  return (
    <div className="relative">
      {isEditing && onRemove && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 z-10"
          onClick={onRemove}
        >
          <X className="h-3 w-3" />
        </Button>
      )}
      <CardWrapper
        {...cardProps}
        className="block border rounded-lg overflow-hidden hover:bg-accent/50 transition-colors"
      >
        <div className="flex flex-col sm:flex-row">
          {preview.image && (
            <div className="sm:w-32 sm:flex-shrink-0">
              <img
                src={preview.image}
                alt={preview.title || 'Link preview'}
                className="w-full h-32 sm:h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}
          <div className="p-3 flex-1 min-w-0">
            {preview.title && (
              <p className="font-medium text-sm line-clamp-2">{preview.title}</p>
            )}
            {preview.description && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {preview.description}
              </p>
            )}
            <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
              <ExternalLink className="h-3 w-3" />
              <span className="truncate">{preview.domain}</span>
            </div>
          </div>
        </div>
      </CardWrapper>
    </div>
  );
};

export default LinkPreviewCard;
